// Shared backtest state for the Discover step. Both the Scan body and the Manual body call
// `run()` to backtest the CURRENTLY ASSEMBLED strategy (whatever is in the draft) as one
// netting account (direction: "both"), and the parent renders a single <BacktestPane> from
// this hook's state. `reset()` is called whenever the assembled strategy changes so a stale
// report never lingers next to edited conditions.

import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { runBacktest } from "../api/backtests";
import { toSpec } from "../components/builder/ReviewBar";
import type { BacktestResponseDto } from "../api/types";
import type { BuilderDraft } from "../state/BuilderState";

export interface DraftBacktestState {
  running: boolean;
  result: BacktestResponseDto | null;
  open: boolean;
  run: (draft: BuilderDraft) => Promise<void>;
  close: () => void;
  reset: () => void;
}

export function useDraftBacktest(): DraftBacktestState {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<BacktestResponseDto | null>(null);
  const [open, setOpen] = useState(false);
  // Monotonic token: only the LATEST run may write state. Stops an earlier in-flight backtest from
  // overwriting a newer one, or resurrecting a result after reset() closed the pane.
  const runToken = useRef(0);

  const run = useCallback(async (draft: BuilderDraft) => {
    const token = ++runToken.current;
    setRunning(true);
    setOpen(true);
    try {
      const res = await runBacktest({
        strategy: toSpec(draft),
        direction: "both",
        minOccurrences: 5,
      });
      if (token !== runToken.current) return; // a newer run (or reset) superseded this one
      setResult(res);
      const s = res.strategy;
      toast.success("Backtest complete", {
        description: s
          ? `${s.trades} trades (${s.longTrades}L/${s.shortTrades}S, ${s.reversals} rev), total ${s.totalReturn.toFixed(2)}%`
          : `${res.long?.trades ?? 0}L / ${res.short?.trades ?? 0}S trades`,
      });
    } catch (err) {
      if (token !== runToken.current) return;
      toast.error("Backtest failed", { description: (err as Error).message });
      setOpen(false);
    } finally {
      if (token === runToken.current) setRunning(false);
    }
  }, []);

  const close = useCallback(() => setOpen(false), []);

  // Invalidate a stale report when the assembled strategy changes (condition picked, scan re-run).
  // Bumping the token also cancels any in-flight run from writing back.
  const reset = useCallback(() => {
    runToken.current++;
    setResult(null);
    setOpen(false);
    setRunning(false);
  }, []);

  return { running, result, open, run, close, reset };
}
